import type {TravelType} from '@/modules/common/context/QueryParams/models';
import type {DeepPartial} from '@/types';
import type {
  SearchFormConfig,
  SearchFormValues,
} from '@travel-frontend/ui-components';

import {
  SearchFormActionTypes,
  searchFormScopedAction,
  searchFormSelectors,
  useSearchForm as useSearchFormShared,
} from '@travel-frontend/ui-components';
import {merge, noop} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {useSearchFormConfig} from '@/modules/common/components/SearchBar/hooks/useSearchFormConfig';
import {useSearchFormDefaultValues} from '@/modules/common/components/SearchBar/hooks/useSearchFormDefaultValues';
import {useAppSelector} from '@/redux/config';

type UseSearchFormProps = {
  travelType: TravelType;
  config?: DeepPartial<SearchFormConfig>;
  prevValues?: SearchFormValues;
  onSubmit?: (state: any) => void;
  onValueChange?: ({field, value}: {field: string; value: any}) => void;
  onClickLastSearchesItem?: (state: any) => void;
};

type UseSearchFormReturnValue = ReturnType<typeof useSearchFormShared>;

const fieldNameByEvent = {
  [SearchFormActionTypes.TravelTypeChanged]: 'travelType',
  [SearchFormActionTypes.destinationField.ValueChanged]: 'destination',
  [SearchFormActionTypes.airportsField.ValueChanged]: 'airports',
  [SearchFormActionTypes.travelPeriodField.ValueChanged]: 'travelPeriod',
  [SearchFormActionTypes.travellersField.ValueChanged]: 'travellers',
  [SearchFormActionTypes.travellersFieldMultiroom.ValueChanged]:
    'travellersMultiroom',
} as const;

export const useSearchForm = ({
  travelType,
  prevValues,
  config: configProperty,
  onSubmit = noop,
  onValueChange = noop,
  onClickLastSearchesItem = noop,
}: UseSearchFormProps): UseSearchFormReturnValue => {
  const dispatch = useDispatch();

  // @ts-expect-error type incompatibility, the selector only expects a the searchform state but not other things
  const values = useAppSelector(searchFormSelectors.getValues);
  const defaultValues = useSearchFormDefaultValues({travelType});
  const config = useSearchFormConfig({
    travelType,
    config: useMemo(
      () =>
        merge(
          {},
          {
            /**
             * @FIXME
             * should be the default in toolkit going forward
             * the entire option can be removed
             */
            multiroom: true,
            travelPeriodField: {
              defaultDuration:
                defaultValues.travelPeriod?.duration ?? undefined,
            },
            travelTypeField: {
              mode: 'list',
            },
          },
          configProperty
        ),
      [defaultValues.travelPeriod?.duration, configProperty]
    ),
  });

  return useSearchFormShared({
    travelType,
    values,
    prevValues,
    defaultValues,
    config,
    dispatch: useCallback(
      (action) => {
        const type = action.type as string;

        dispatch(searchFormScopedAction(action, {travelType, defaultValues}));

        if (
          type === SearchFormActionTypes.TravelTypeChanged ||
          type === SearchFormActionTypes.destinationField.ValueChanged ||
          type === SearchFormActionTypes.airportsField.ValueChanged ||
          type === SearchFormActionTypes.travelPeriodField.ValueChanged ||
          type === SearchFormActionTypes.travellersField.ValueChanged ||
          type === SearchFormActionTypes.travellersFieldMultiroom.ValueChanged
        ) {
          const field = fieldNameByEvent[type as keyof typeof fieldNameByEvent];
          const value =
            type === SearchFormActionTypes.TravelTypeChanged // @FIXME unify travel type action format in component-library
              ? action.payload.travelType
              : action.payload.value;

          onValueChange({
            field,
            value,
          });
        }
      },
      [dispatch, travelType, defaultValues, onValueChange]
    ),
    onSubmit,
    onClickLastSearchesItem,
  });
};
